import classNames from "classnames";
import { partial } from "lodash";
import React, { FC } from "react";
import { FormattedMessage } from "react-intl";

import {
  MessageTheme,
  SelectedWindows,
  useChatLeftMenuSettings,
  useChatMessageTheme,
  useChatSelectedWindow,
  useChatToRename,
  useSelectedChat,
} from "~/ui/context";
import { useCurrentUser, useScreenMd } from "~/utils";

import { Button } from "../../buttons";
import { Icon } from "../../icons";

interface ChatHeaderProps {
  onClose: () => void;
}

export const ChatHeader: FC<ChatHeaderProps> = ({ onClose }) => {
  const { setSelectedChat } = useSelectedChat();
  const { isMenuOpened, setIsMenuOpened } = useChatLeftMenuSettings();
  const { selectedWindow, setSelectedWindow } = useChatSelectedWindow();
  const { setChatToRename } = useChatToRename();
  const { messageTheme, toggleMessageTheme } = useChatMessageTheme();
  const isDesktop = useScreenMd();
  const currentUser = useCurrentUser();
  const handleCreateChat = () => {
    setSelectedChat(null);
    setSelectedWindow(SelectedWindows.CreateChat);
    setChatToRename(null);
  };
  const createSystemChat = () => {
    setSelectedChat(null);
    setSelectedWindow(SelectedWindows.SystemChat);
    setChatToRename(null);
  };
  return (
    <div
      className={classNames(
        "flex items-center borderBottom h-5 pd-md h-min-5",
        isDesktop ? "justify-between" : "justify-between"
      )}
    >
      <div className="w-90 flex items-center">
        {!isDesktop &&
          selectedWindow !== SelectedWindows.CreateChat &&
          selectedWindow !== SelectedWindows.SystemChat && (
            <Icon
              onClick={partial(setIsMenuOpened, !isMenuOpened)}
              icon={["fal", "bars"]}
              className="nav-menu-icon"
            />
          )}
        {selectedWindow === SelectedWindows.CreateChat && (
          <div className="flex items-center gap-sm">
            <Icon
              onClick={partial(setSelectedWindow, SelectedWindows.None)}
              icon={["far", "angle-left"]}
              size="2x"
            />
            <h6 className="font-variant-small-caps">
              <FormattedMessage
                defaultMessage="Creating a new conversation"
                id="2reX4R"
              />
            </h6>
          </div>
        )}
        {selectedWindow === SelectedWindows.SystemChat && (
          <div className="flex items-center gap-sm">
            <Icon
              onClick={partial(setSelectedWindow, SelectedWindows.None)}
              icon={["far", "angle-left"]}
              size="2x"
            />
            <h6 className="font-variant-small-caps">
              <FormattedMessage
                defaultMessage="Creating a new system chat"
                id="KC5GBy"
              />
            </h6>
          </div>
        )}

        {selectedWindow !== SelectedWindows.CreateChat &&
          selectedWindow !== SelectedWindows.SystemChat && (
            <div className="flex justify-start gap-sm mg-l-md items-center w-90">
              <Button
                variant="success"
                size="md"
                className="flex gap-md items-center font-variant-small-caps pd-t-sm pd-b-sm"
                onClick={handleCreateChat}
              >
                <FormattedMessage defaultMessage="New" id="bW7B87" />
                <Icon icon={["far", "paper-plane"]} size="lg" />
              </Button>

              {currentUser?.user_id === 3 && (
                <Button
                  variant="success"
                  size="md"
                  className="flex gap-md items-center font-variant-small-caps pd-t-sm pd-b-sm"
                  onClick={createSystemChat}
                >
                  <FormattedMessage defaultMessage="System" id="+CwN9C" />
                  <Icon icon={["far", "paper-plane"]} size="lg" />
                </Button>
              )}

              {selectedWindow === SelectedWindows.Conversation && (
                <Icon
                  icon={
                    messageTheme === MessageTheme.Dark
                      ? ["fad", "light-switch-on"]
                      : ["fad", "light-switch-off"]
                  }
                  size="2x"
                  className="pointer"
                  onClick={toggleMessageTheme}
                />
              )}
            </div>
          )}
      </div>
      <Icon
        icon={["fal", "times"]}
        size="lg"
        color="primary"
        className="pointer pd-r-sm"
        onClick={onClose}
      />
    </div>
  );
};
